// ── imports explicites (C4 — générés par esm-codemod --analyze, maintenus à la main) ──
import { useEffect, useRef, useState } from "../00-boot.jsx";
import { langPick } from "../01-taxonomy-cities.jsx";
import { profileNudgeSeen } from "../07-trust-moderation.jsx";
import { authCallbackUrl, ecrireCarte, lanacCarteAuDemarrage, lanacSupabaseClient, lncCaptchaToken } from "../08-supabase-client.jsx";
import { SUPPORTED_LOCALES } from "../i18n/loader.jsx";
import { tx } from "../04-i18n-dict.jsx";
// ── Tranche 3 · useSession : identité, boot auth, vérification email, recovery ─
//    Possède `me` (le profil connecté) et tout l'état d'écran d'auth. L'effet de
//    BOOT session (getSession/onAuthStateChange) reste dans App() : il appelle
//    restoreProfileFromSession (fonction d'App) — il consomme pendingVerificationRef
//    retourné ici. Corps copiés tels quels de App().
/** @param {{lang?:string}} [ports] — `lang` vient de useUiPrefs, appelé AVANT ce hook. */
function useSession(ports={}){
  const {lang}=ports;
  //  (B-HORS-LIGNE-1, 03/11/2026) LA CARTE DU MEMBRE (08-supabase-client) : l'ouverture part de la carte gardée sur l'appareil —
  //   synchrone, avant tout réseau — `provisoire` jusqu'au profil frais, qui la REMPLACE puis la réécrit (ici, à chaque profil frais).
  const [me,setMe]=useState(()=>lanacCarteAuDemarrage());
  const [authChecking,setAuthChecking]=useState(true); // boot : vérification de session en cours (évite l'écran blanc + le flash de l'écran de connexion)
  useEffect(()=>{ if(me) setAuthChecking(false); },[me]);
  useEffect(()=>{ if(me&&!me.provisoire) ecrireCarte(me); },[me]);
  const [pendingSignup,setPendingSignup]=useState(null);
  const pendingVerificationRef=useRef(false);
  useEffect(()=>{pendingVerificationRef.current=!!me?.pendingVerification;},[me?.pendingVerification]);
  const [authEmailStatus,setAuthEmailStatus]=useState(null);
  const [authEmailLoading,setAuthEmailLoading]=useState(false);
  const [authEmailCooldownUntil,setAuthEmailCooldownUntil]=useState(0);
  const [authEmailTick,setAuthEmailTick]=useState(0);
  useEffect(()=>{
    if(!authEmailCooldownUntil)return;
    const id=setInterval(()=>{
      setAuthEmailTick(t=>t+1);
      if(Date.now()>=authEmailCooldownUntil) clearInterval(id);
    },1000);
    return()=>clearInterval(id);
  },[authEmailCooldownUntil]);
  const [emailCodeSent,setEmailCodeSent]=useState(false);
  const [passwordRecovery,setPasswordRecovery]=useState(false);
  const [newRecoveryPwd,setNewRecoveryPwd]=useState("");
  const [newRecoveryPwd2,setNewRecoveryPwd2]=useState("");
  const [recoverySaving,setRecoverySaving]=useState(false);
  const [verifyGate,setVerifyGate]=useState(null);
  const [showOnboarding,setShowOnboarding]=useState(false);
  const [profileCompletion,setProfileCompletion]=useState(false);
  useEffect(()=>{if(me?.provisoire)return;if(me?.profileIncomplete&&!profileNudgeSeen(me.id))setProfileCompletion(true);},[me?.id,!!me?.provisoire]); // V3-T3b · (B-HORS-LIGNE-1) décidée sur le profil FRAIS, jamais sur la carte
  const [profileValidationToken,setProfileValidationToken]=useState(null);
  // BLOC1-1 · Persiste la langue choisie dans profiles.locale → les emails partent dans la langue du destinataire.
  // Gardé : aucune écriture au montage/connexion (seulement sur un vrai changement de langue) pour ne pas générer d'egress inutile.
  // `lastPersistedLocaleRef` n'était lu QUE par cet effet → il déménage avec lui (aucun retour nécessaire).
  const lastPersistedLocaleRef=useRef(null);
  useEffect(()=>{
    if(!me?.id) return;
    if(lastPersistedLocaleRef.current===null){lastPersistedLocaleRef.current=lang;return;}
    if(lastPersistedLocaleRef.current===lang) return;
    lastPersistedLocaleRef.current=lang;
    const client=lanacSupabaseClient();
    if(!client||!SUPPORTED_LOCALES.includes(lang)) return; // liste centrale (i18n/loader) — une 5e langue est couverte sans toucher ce hook
    try{const q=client.from("profiles").update({locale:lang}).eq("id",me.id);q&&q.then&&q.then(undefined,()=>{});}catch(_){}
  },[lang,me?.id]);
  // ── C1 (17/07/2026) — appSendPasswordResetEmail remonté de App(), verbatim.
  //  Sémantique : AUTH (reset de mot de passe) ⇒ useSession, pas useUiPrefs (la règle
  //  d'attribution ne voyait que `lang`, déjà un port de ce hook). Fermeture 0 ⇒ zéro
  //  nouveau port. E-mail RÉEL ⇒ jamais exercé par l'e2e (déclaré au plan de clôture).
  const appSendPasswordResetEmail=async email=>{
    const cleanEmail=(email||"").trim().toLowerCase();
    const client=lanacSupabaseClient();
    if(!client) return {ok:false,error:tx("tax.service-indisponible_fe26d5",lang)};
    try{
      const {error}=await client.auth.resetPasswordForEmail(cleanEmail,{redirectTo:authCallbackUrl("recovery"),captchaToken:await lncCaptchaToken()});
      if(error) return {ok:false,error:error.message};
      return {ok:true};
    }catch(e){return {ok:false,error:e?.message||tx("tax.email-de-reinitialisatio_3c392f",lang)};}
  };
  return {appSendPasswordResetEmail,
    me,setMe,authChecking,setAuthChecking,pendingSignup,setPendingSignup,pendingVerificationRef,
    authEmailStatus,setAuthEmailStatus,authEmailLoading,setAuthEmailLoading,
    authEmailCooldownUntil,setAuthEmailCooldownUntil,authEmailTick,setAuthEmailTick,
    emailCodeSent,setEmailCodeSent,passwordRecovery,setPasswordRecovery,
    newRecoveryPwd,setNewRecoveryPwd,newRecoveryPwd2,setNewRecoveryPwd2,
    recoverySaving,setRecoverySaving,verifyGate,setVerifyGate,
    showOnboarding,setShowOnboarding,profileCompletion,setProfileCompletion,
    profileValidationToken,setProfileValidationToken};
}
export { useSession };
